import React, { type RefObject, Suspense, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type { PlaylistQuery as PlaylistQueryType } from './__generated__/PlaylistQuery.graphql';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '406px',
		display: 'flex',
		flexDirection: 'column',
		overflowY: 'auto',
		position: 'relative',
	},
	fullHeightWrapper: {
		flexGrow: 1,
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	currentItem: PlaylistItemData | null;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
};

const PlaylistWrapper = ({
	children,
	currentItem,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
	audioRef,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const handleToggle = () => setIsExpanded((prev) => !prev);

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{!isExpanded && hasOtherActivePlayback && currentItem && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={currentItem?.title || ''}
						byline=""
						avatarUrl={''}
						elevated
						audioRef={audioRef}
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex xcss={styles.contentContainer}>
						{/* Scroll shadow effect */}
						<Box xcss={styles.coverBorder} />
						<Flex direction="column" xcss={styles.fullHeightWrapper}>
							<Box xcss={styles.stickyShadow} />
							{children}
						</Flex>
					</Flex>
				)}
			</div>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	currentItem: PlaylistItemData | null;
	setCurrentItem: (item: PlaylistItemData | null) => void;
	audioRef: RefObject<HTMLAudioElement>;
};

const PlaylistInner = ({ cloudId, currentItem, setCurrentItem, audioRef }: PlaylistInnerProps) => {
	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainContent {
								title
								history {
									createdBy {
										displayName
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'store-or-network',
		},
	);

	const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];

	const playlistItems: PlaylistItemData[] = tracks.map((track: any) => {
		const mainContent = track.mainContent;
		const creator = mainContent?.history?.createdBy;

		return {
			id: track.id,
			title: mainContent?.title || undefined,
			type: track.type,
			author: creator?.displayName || undefined,
		};
	});

	const handleItemClick = (item: PlaylistItemData) => {
		setCurrentItem(item);
	};

	return (
		<Flex direction="column">
			{playlistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={playlistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
					audioRef={audioRef}
				/>
			)}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
};

export const Playlist = ({
	cloudId,
	defaultExpanded,
	hasOtherActivePlayback,
	audioRef,
}: PlaylistProps) => {
	const [currentItem, setCurrentItem] = useState<PlaylistItemData | null>(null);

	return (
		<PlaylistWrapper
			currentItem={currentItem}
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={hasOtherActivePlayback}
			audioRef={audioRef}
		>
			<ErrorBoundary
				fallbackRender={(props) => <PlaylistErrorView {...props} onRetry={() => {}} />}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner
						cloudId={cloudId}
						currentItem={currentItem}
						setCurrentItem={setCurrentItem}
						audioRef={audioRef}
					/>
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
